import { Modal } from "antd";
import { Paragraph } from "components/atoms/Typography";
import { useUpload } from "context/UploadContext";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import styles from "./DocumentUploadModal.module.scss";
import DocumentUploadModalFooter from "./DocumentUploadModalFooter";

interface DocumentUploadModalProps {
    modalIsOpen: boolean;
    onCancel: () => void;
}

interface UploadFile {
    file: File;
    description?: string;
}

function DocumentUploadModal({ modalIsOpen, onCancel }: DocumentUploadModalProps) {
    const buildingModelUid = useBuildingModelUid();
    const [files, setFiles] = useState<UploadFile[]>([]);
    const { startUpload } = useUpload();

    if (!buildingModelUid) throw new Error("Missing buildingModel");

    const MAX_FILE_SIZE = 20 * 1024 * 1024 * 1024; // 20GB

    const onDrop = (acceptedFiles: File[]) => {
        const newFiles = acceptedFiles.map((file) => ({
            file,
            description: "",
        }));
        setFiles((previousState) => [...previousState, ...newFiles]);
    };

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        maxSize: MAX_FILE_SIZE,
    });

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const modalSubmitHandler = () => {
        if (files.length === 0) return;

        files.forEach((uploadFile) => {
            startUpload({
                file: uploadFile.file,
                buildingModelUid,
                description: uploadFile.description,
            });
        });

        setFiles([]);
        onCancel();
    };

    const handleCancel = () => {
        setFiles([]);
        onCancel();
    };

    const updateDescrition = (index: number, description: string) => {
        setFiles((prev) => prev.map((item, i) => (i === index ? { ...item, description } : item)));
    };

    return (
        <Modal
            open={modalIsOpen}
            onCancel={handleCancel}
            title="Upload document"
            // className={styles.modalContainer}
            footer={
                <DocumentUploadModalFooter onSave={() => modalSubmitHandler()} onCancel={onCancel} isLoading={false} />
            }
        >
            <div className={styles.supportedTypes}>
                <Paragraph size="small" style={{ color: "var(--audette-gray-500)" }}>
                    Supported file types: PDF, DOCX, TXT, PPTX, XLSX (1 sheet), CSV, JSON and HTML.
                </Paragraph>
            </div>

            <div
                {...getRootProps()}
                className={styles.dropzone}
                style={{
                    backgroundColor: isDragActive ? "#e6f7ff" : "transparent",
                }}
            >
                <input {...getInputProps()} />
                {isDragActive ? <p>Drop the files here...</p> : <p>Drag & drop files, or click to select files.</p>}
            </div>

            <Paragraph size="small" style={{ color: "var(--audette-gray-500)", marginTop: "12px" }}>
                Note: Only documents under 1GB can be parsed and attached to the chatbot.
            </Paragraph>

            {fileRejections.length > 0 && (
                <div className={styles.error}>
                    {fileRejections.map(({ file }) => (
                        <p key={file.name}>{file.name}: File exceeds 20GB limit</p>
                    ))}
                </div>
            )}

            {files.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                    <p style={{ fontWeight: 500, marginBottom: "8px" }}>Selected files:</p>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {files.map((item, index) => (
                            <li key={item.file.name + item.file.lastModified} className={styles.uploadedFile}>
                                <div>
                                    <span>{item.file.name}</span>
                                    <input
                                        className={styles.descriptionInput}
                                        placeholder="Add description (optional)"
                                        value={item.description}
                                        onChange={(e) => updateDescrition(index, e.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeFile(files.indexOf(item))}
                                    className={styles.removeButton}
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    );
}

export default DocumentUploadModal;
